//src/views/Clientes/DescuentoFlota/EliminarDescuentoFlota.jsx
import React, { useEffect, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

/**
 * Props:
 * - id: ID del descuento a eliminar
 * - onVolver / onClose: cerrar modal
 * - onEliminado: callback al eliminar OK
 */
export default function EliminarDescuentoFlota({
  id,
  onVolver,
  onClose,
  onEliminado,
}) {
  const closeFn = onClose || onVolver || (() => {});
  const eliminadoFn = onEliminado || (() => {});

  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const [d, setD] = useState(null);

  // ============================
  //        CARGA INICIAL
  // ============================
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const dto = await apiFetchJson(
          apiRoutes.descuentoFlota.detail(id)
        );
        setD(dto);
      } catch (e) {
        setError(e?.message || "Error al cargar el descuento");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ============================
  //        ELIMINAR
  // ============================
  const handleEliminar = async () => {
    if (!id) return;

    setRemoving(true);
    setError("");

    try {
      await appFetchJson(
        apiRoutes.descuentoFlota.delete(id),
        { method: "DELETE" }
      );

      await Promise.resolve(eliminadoFn());
      closeFn();
    } catch (e) {
      const status = e?.status ?? e?.response?.status;

      if (status === 404) {
        setError("El descuento no existe o ya fue eliminado.");
      } else if (status === 403) {
        setError("No tiene permiso para eliminar este descuento.");
      } else if (status === 409) {
        setError(e?.message || "No se puede eliminar el descuento (en uso).");
      } else {
        setError(e?.message || "No se pudo eliminar el descuento.");
      }
      setRemoving(false);
    }
  };

  // ============================
  //        RENDER
  // ============================
  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="section-title text-red-700">
            Eliminar Descuento Flota
          </h2>
        </div>

        {/* Body */}
        <div className="modal-body space-y-4">
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            ¿Está seguro que desea eliminar este registro?
          </div>

          {error && (
            <div className="text-sm text-red-700">{error}</div>
          )}

          {d && (
            <div className="form-grid">
              <Fila label="Cliente" value={d.clienteNombre} />
              <Fila label="EE.SS." value={d.eessNombre || "TODAS"} />
              <Fila label="Producto" value={d.productoNombre || "TODOS"} />
              <Fila label="Tarjeta" value={d.tarjetaNumero || "TODAS"} />

              <Fila label="Total Valor PYG" value={d.valorDescuento} />
              <Fila label="Valor EE.SS. PYG" value={d.valorEess} />
              <Fila label="Valor Petrobras PYG" value={d.valorPetr} />

              <Fila label="Total Descuento %" value={d.porcentajeDescuento} />
              <Fila label="EE.SS. %" value={d.porcentajeEess} />
              <Fila label="Petrobras %" value={d.porcentajePetr} />

              <Fila label="Fecha Inicio" value={d.fechaInicio} />
              <Fila label="Fecha Fin" value={d.fechaFin} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
                label={removing ? "Eliminando…" : "Eliminar"}
                saving={removing}
                isValid={!removing}
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleEliminar}
            />

            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */

function Fila({ label, value }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1 py-2 text-gray-800">
        {value ?? "—"}
      </div>
    </div>
  );
}
